import { useEffect, useRef, useState } from 'react';

/**
 * Count-up animation (PLAN.md M7 MOTION — "count-up on the success headline"). Animates a number
 * from 0 (or a previous value) to `value` over `duration`, honouring reduced-motion (snaps
 * instantly). Render-prop / formatter so the caller controls the displayed string (e.g. percent).
 */
export interface CountUpProps {
  value: number;
  /** ms; default 900. */
  duration?: number;
  /** Format the in-flight number to a string. Default: rounded integer. */
  format?: (n: number) => string;
  className?: string;
}

export function CountUp({
  value,
  duration = 900,
  format = (n) => String(Math.round(n)),
  className,
}: CountUpProps): React.ReactElement {
  const [display, setDisplay] = useState(value);
  const fromRef = useRef(0);

  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setDisplay(value);
      fromRef.current = value;
      return;
    }
    const from = fromRef.current;
    const start = performance.now();
    let raf = 0;
    const tick = (now: number): void => {
      const t = Math.min(1, (now - start) / duration);
      // easeOutCubic
      const eased = 1 - (1 - t) ** 3;
      setDisplay(from + (value - from) * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
      else fromRef.current = value;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);

  return <span className={className}>{format(display)}</span>;
}
